import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

// Must match the b2b_refund_requests_reason_check constraint exactly —
// the DB rejects anything else at insert time.
export type RefundReason =
  | 'session_not_started'
  | 'technical_issue'
  | 'very_short_session'
  | 'duplicate_charge'
  | 'wrong_session'
  | 'other';

// Matches b2b_refund_requests_status_check.
export type RefundStatus = 'pending' | 'approved' | 'rejected' | 'auto_approved';

export interface NeuraxRefund {
  id: string;
  session_id: string | null;
  reason: RefundReason | string;
  reason_details: string | null;
  status: RefundStatus | string;
  session_amount: number | null;
  session_type: string | null;
  requested_by: string | null;
  /** Resolved from the company's b2b_employees; 'F1Jobs team' when filed by staff. */
  requested_by_name: string | null;
  reviewed_at: string | null;
  created_at: string;
}

async function fetchRefunds(companyId: string): Promise<NeuraxRefund[]> {
  // requested_by is an auth uid — resolve it against the company's employee
  // roster (readable under RLS). Filed-by-staff requests won't match and are
  // labelled as F1Jobs.
  const [refundsRes, employeesRes] = await Promise.all([
    supabase
      .from('b2b_refund_requests')
      .select(
        'id, session_id, reason, reason_details, status, session_amount, session_type, requested_by, reviewed_at, created_at'
      )
      .eq('b2b_company_id', companyId)
      .order('created_at', { ascending: false }),
    supabase
      .from('b2b_employees')
      .select('user_id, full_name, email')
      .eq('b2b_company_id', companyId),
  ]);
  if (refundsRes.error) throw new Error(`b2b_refund_requests: ${refundsRes.error.message}`);

  // Employee lookup is best-effort — never let it hide the refunds list.
  const nameByUser = new Map(
    (
      (employeesRes.data ?? []) as {
        user_id: string | null;
        full_name: string | null;
        email: string;
      }[]
    )
      .filter((e) => e.user_id)
      .map((e) => [e.user_id as string, e.full_name || e.email])
  );

  return ((refundsRes.data ?? []) as Omit<NeuraxRefund, 'requested_by_name'>[]).map((r) => ({
    ...r,
    requested_by_name: r.requested_by ? (nameByUser.get(r.requested_by) ?? 'F1Jobs team') : null,
  }));
}

export function useRefunds() {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'refunds', companyId],
    queryFn: () => fetchRefunds(companyId),
    staleTime: 30_000,
  });
}

export interface CreateRefundInput {
  session_id: string;
  reason: RefundReason;
  reason_details?: string | null;
  session_amount: number;
  session_type: 'interview' | 'online_assessment' | string;
}

export function useCreateRefund() {
  const qc = useQueryClient();
  const { companyId } = useBranding();
  return useMutation({
    mutationFn: async (input: CreateRefundInput) => {
      const userRes = await supabase.auth.getUser();
      const requestedBy = userRes.data.user?.id ?? null;

      const row = {
        b2b_company_id: companyId,
        session_id: input.session_id,
        reason: input.reason,
        reason_details: input.reason_details ?? null,
        session_amount: input.session_amount,
        session_type: input.session_type,
        requested_by: requestedBy,
        status: 'pending',
      };

      const { data, error } = await supabase
        .from('b2b_refund_requests')
        .insert(row)
        .select()
        .single();

      if (error) throw new Error(`b2b_refund_requests: ${error.message}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['neurax', 'refunds'] });
    },
  });
}
